import React from "react";
import { Button, Checkbox, Form, FormInstance, Input, Popconfirm, Select, Typography } from "antd";
import { IAntennaForm } from "store/readers-tags/readers/readers-model";
import useTranslation from "utils/hooks/useTranslation";
import { antennaNumberValidator } from "../../utils";
import * as S from "../index.styled";
import { accessPointOptionsAntenna, flowOptions, sectorOptions, selectAntennaNumberList } from "../types";
import { handleExcludeAntenna } from "./utils";

export interface IPropsFormAntenna {
  field: any;
  formAntenna: FormInstance;
  collapseKey: string;
  readersRemoveAntenna: (...args) => void;
  setActiveCollapseKeys: React.Dispatch<React.SetStateAction<string[]>>;
}

interface IDispatchProps {
  readersRemoveAntenna: (...args) => void;
  setActiveCollapseKeys: React.Dispatch<React.SetStateAction<string[]>>;
}

export type IFormProps = IPropsFormAntenna & IDispatchProps;

const FormAntenna = ({ field, formAntenna, collapseKey, readersRemoveAntenna, setActiveCollapseKeys }: IFormProps) => {
  const { Title } = Typography;
  const ANTENNA_MODEL = useTranslation({ id: "antenna_model" });
  const SERIE_NUMBER = useTranslation({ id: "serie_number" });
  const INSERT_PORT_NUMBER = useTranslation({ id: "insert_port_number" });
  const INSERT_MODEL = useTranslation({ id: "insert_model" });
  const DELETE_ANTENNA = useTranslation({ id: "delete_antenna" });
  const OPTIONAL = useTranslation({ id: "optional" });
  const ACTIVE = useTranslation({ id: "active" });
  const INSERT_SERIE_NUMBER = useTranslation({ id: "insert_serie_number" });
  const SELECT_A_OPTION = useTranslation({ id: "select_a_option" });
  const ANTENNA_NAME = useTranslation({ id: "antenna_name" });
  const SELECT_READER_NAME = useTranslation({ id: "select_reader_name_in_reader_tab" });
  const SECTOR = useTranslation({ id: "sector" });
  const READER = useTranslation({ id: "reader" });
  const ANTENNA_NUMBER = useTranslation({ id: "antenna_number" });
  const REQUIRED_FIELD = useTranslation({ id: "required_field" });
  const DELETE_ANTENNA_DESCRIPTION = useTranslation({ id: "do_you_want_to_delete_this_antenna" });
  const YES = useTranslation({ id: "yes" });
  const NO = useTranslation({ id: "no" });

  const IGNORE_FIELDS = ["modelDescription", "_slotIndex"];

  const hasAnyFieldFilled = (antenna?: IAntennaForm) => {
    if (!antenna) return false;

    return Object.entries(antenna).some(([key, value]) => {
      if (IGNORE_FIELDS.includes(key)) return false;
      return value !== undefined && value !== null && value !== "";
    });
  };
  const antennas = Form.useWatch("antennas", formAntenna);
  const antennaData = antennas?.[field.name];
  const disableExcludeButton = !hasAnyFieldFilled(antennaData);

  return (
    <>
      <S.FormItem {...field} name={[field.name, "modelDescription"]} label={`${READER}*`}>
        <Input style={S.InputField} data-testid="readerNumber" placeholder={SELECT_READER_NAME} disabled />
      </S.FormItem>

      <S.FormItem
        {...field}
        name={[field.name, "antennaNumber"]}
        label={`${ANTENNA_NUMBER}*`}
        rules={[
          ({ getFieldValue }) => ({
            validator: antennaNumberValidator(getFieldValue, field.name, REQUIRED_FIELD),
          }),
        ]}
      >
        <Select size="large" placeholder={SELECT_A_OPTION} style={S.SelectField} allowClear data-testid="selectAntennaNumber">
          {selectAntennaNumberList.map((option) => (
            <Select.Option value={option.id} key={`antenna-${field.key}-${field.name}-${collapseKey}-number-${option.id}`}>
              {option.description}
            </Select.Option>
          ))}
        </Select>
      </S.FormItem>

      <S.FormItem
        {...field}
        name={[field.name, "antennaPositionType"]}
        label={`${ANTENNA_NAME}*`}
        rules={[
          ({ getFieldValue }) => ({
            validator: antennaNumberValidator(getFieldValue, field.name, REQUIRED_FIELD),
          }),
        ]}
      >
        <Select size="large" placeholder={SELECT_A_OPTION} allowClear style={S.SelectField} data-testid="selectAntennaName">
          {accessPointOptionsAntenna.map((option) => (
            <Select.Option value={option.id} key={`antenna-${field.key}-${field.name}-${collapseKey}-position-${option.id}`}>
              {option.description}
            </Select.Option>
          ))}
        </Select>
      </S.FormItem>

      <S.FormItem {...field} name={[field.name, "sector"]} label={`${SECTOR} (${OPTIONAL})`}>
        <Select size="large" placeholder={SELECT_A_OPTION} allowClear style={S.SelectField} data-testid="selectSectorName">
          {sectorOptions.map((option) => (
            <Select.Option value={option.id} key={`antenna-${field.key}-${field.name}-${collapseKey}-sector-${option.id}`}>
              {option.description}
            </Select.Option>
          ))}
        </Select>
      </S.FormItem>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
        <S.FormItem {...field} name={[field.name, "serieNumber"]} label={`${SERIE_NUMBER} (${OPTIONAL})`}>
          <Input style={S.InputField} data-testid="serie" maxLength={50} placeholder={INSERT_SERIE_NUMBER} />
        </S.FormItem>

        <S.FormItem {...field} name={[field.name, "model"]} label={`${ANTENNA_MODEL} (${OPTIONAL})`}>
          <Input style={S.InputField} data-testid="model" maxLength={50} placeholder={INSERT_MODEL} />
        </S.FormItem>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
        <S.FormItem
          {...field}
          name={[field.name, "flow"]}
          label={`${useTranslation({ id: "flow" })}*`}
          rules={[
            ({ getFieldValue }) => ({
              validator: antennaNumberValidator(getFieldValue, field.name, REQUIRED_FIELD),
            }),
          ]}
        >
          <Select size="large" placeholder={SELECT_A_OPTION} allowClear style={S.SelectField} data-testid="selectFlowName">
            {flowOptions.map((option) => (
              <Select.Option value={option.id} key={`antenna-${field.key}-${field.name}-${collapseKey}-flow-${option.id}`}>
                {option.description}
              </Select.Option>
            ))}
          </Select>
        </S.FormItem>

        <S.FormItem
          {...field}
          name={[field.name, "antennaPort"]}
          label={`${useTranslation({ id: "port" })}*`}
          rules={[
            ({ getFieldValue }) => ({
              validator: antennaNumberValidator(getFieldValue, field.name, REQUIRED_FIELD),
            }),
          ]}
        >
          <Input style={S.InputField} data-testid="port" maxLength={10} placeholder={INSERT_PORT_NUMBER} />
        </S.FormItem>
      </div>

      <S.EventsContainer>
        <div>
          <S.FormCheckbox {...field} name={[field.name, "status"]} valuePropName="checked">
            <Checkbox data-testid="antennaActive" />
          </S.FormCheckbox>
          <Title level={5} style={S.CheckDescription}>
            {ACTIVE}
          </Title>
        </div>
      </S.EventsContainer>

      <S.ExcludeContainer>
        <Popconfirm
          title={DELETE_ANTENNA_DESCRIPTION}
          okText={YES}
          placement="top"
          disabled={disableExcludeButton}
          cancelText={NO}
          onConfirm={() => handleExcludeAntenna(field.name, collapseKey, setActiveCollapseKeys, readersRemoveAntenna, formAntenna)}
        >
          <Button disabled={disableExcludeButton} className="btnExclude" data-testid="excludeAntenna">
            {DELETE_ANTENNA}
          </Button>
        </Popconfirm>
      </S.ExcludeContainer>
    </>
  );
};

export default FormAntenna;
